
import { AiProviderId, ChatMessage } from './types';

interface SendChatParams {
  provider: AiProviderId;
  model: string;
  apiKey: string;
  baseUrl?: string;
  messages: ChatMessage[];
  systemPrompt: string;
}

export const sendChat = async ({
  provider,
  model,
  apiKey,
  baseUrl,
  messages,
  systemPrompt
}: SendChatParams): Promise<string> => {
  
  // Prepare history: system prompt + last 10 messages to keep context window reasonable
  const history = messages.slice(-10);

  if (provider === 'openai' || provider === 'custom') {
    const url = provider === 'custom' && baseUrl ? `${baseUrl}/chat/completions` : 'https://api.openai.com/v1/chat/completions';
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: model,
        messages: [
          { role: 'system', content: systemPrompt },
          ...history.map(m => ({ role: m.role, content: m.content }))
        ],
        temperature: 0.7
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error?.message || 'OpenAI API Error');
    }

    const data = await response.json();
    return data.choices[0]?.message?.content || '';
  }

  if (provider === 'anthropic') {
    // Note: Anthropic often requires a proxy for browser calls due to CORS, 
    // unless they have enabled browser access recently. 
    // This assumes the user understands BYOK browser limitations.
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'content-type': 'application/json',
        // 'dangerously-allow-browser': 'true' // Sometimes required by SDKs, but this is raw fetch
      },
      body: JSON.stringify({
        model: model,
        system: systemPrompt,
        messages: history.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content })),
        max_tokens: 1024
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error?.message || 'Anthropic API Error (Check Console/CORS)');
    }

    const data = await response.json();
    return data.content[0]?.text || '';
  }

  if (provider === 'gemini') {
    // Google GenAI API
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;
    
    const contents = [
      { role: 'model', parts: [{ text: `System Instruction: ${systemPrompt}` }] }, // Gemini "hack" for system prompt in chat
      ...history.map(m => ({
        role: m.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: m.content }]
      }))
    ];

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error?.message || 'Gemini API Error');
    }

    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  }

  throw new Error('Unknown provider');
};
